import { RequestMethod } from '../../enums/request-method';
import { IRestRequest } from '../../interfaces/rest/rest-request';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';

/**
 * Configuration for a HubspotWebhook task — an entry trigger that receives HubSpot
 * webhook events, validates the HMAC signature, and starts a flow for each event.
 * Optionally deduplicates property-change events that fire alongside creation events.
 */
export class HubSpotWebhookConfig extends DestinationTaskConfigBase {
	/**
	 * HTTP method for the webhook endpoint. Managed by the receiver infrastructure.
	 * @default RequestMethod.GET
	 */
	httpMethod = RequestMethod.GET;

	/**
	 * The segment that will be appended to the base webhook URL.
	 * @default 'default'
	 */
	endpoint = 'default';

	/**
	 * Full resolved webhook URL. Server-computed from endpoint + microservice prefix.
	 * Not set by the user.
	 * @default ''
	 */
	url = '';

	/**
	 * When false (default), the webhook endpoint returns HTTP 202 immediately and the flow
	 * runs asynchronously. When true, the HTTP connection is held open until the flow
	 * completes and the response is returned to HubSpot.
	 * @default false
	 */
	isSync = false;

	/**
	 * Optional sample HTTP request used by the flow simulator for testing.
	 */
	sampleData?: IRestRequest;

	/**
	 * HubSpot app client secret used to verify the HMAC-SHA256 webhook signature.
	 * Resolved via GETSECRET() expression at runtime.
	 * @default ''
	 */
	clientSecret = '';

	/**
	 * When true, if a webhook batch contains both a creation event and a non-creation event
	 * (e.g. propertyChange) for the same object at the same timestamp, the non-creation event
	 * is dropped. Prevents duplicate processing on initial object creation.
	 * @default true
	 */
	consolidateCreateEvent = true;

	constructor(init?: Partial<HubSpotWebhookConfig>) {
		super(init);
		if (init) {
			Object.assign(this, init);
		}
	}
}
